import { useParams, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Avatar,
  Chip,
  Rating,
  Button,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import {
  Sync as SyncIcon,
  Verified as VerifiedIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { leaguesApi } from '../api/leagues';

export default function LeaguePage() {
  const { leagueId } = useParams<{ leagueId: string }>();
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['league', leagueId],
    queryFn: () => leaguesApi.getLeague(leagueId!),
    enabled: !!leagueId,
  });

  const refreshMutation = useMutation({
    mutationFn: () => leaguesApi.refreshLeague(leagueId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['league', leagueId] });
    },
  });

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !data) {
    return (
      <Alert severity="error">
        League not found or failed to load.
      </Alert>
    );
  }

  const { league, members } = data;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'in_season':
        return 'success';
      case 'complete':
        return 'default';
      case 'drafting':
        return 'warning';
      default:
        return 'info';
    }
  };

  return (
    <Box>
      {/* League Header */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                {league.name}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip label={`Season ${league.season}`} size="small" />
                <Chip
                  label={league.status.replace('_', ' ')}
                  size="small"
                  color={getStatusColor(league.status) as any}
                />
                <Chip label={`${league.total_rosters} teams`} size="small" variant="outlined" />
              </Box>
            </Box>
            <Button
              startIcon={<SyncIcon />}
              onClick={() => refreshMutation.mutate()}
              disabled={refreshMutation.isPending}
            >
              {refreshMutation.isPending ? 'Refreshing...' : 'Refresh'}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Members Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            League Members
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>User</TableCell>
                  <TableCell>Team Name</TableCell>
                  <TableCell align="center">Role</TableCell>
                  <TableCell align="center">Trust Score</TableCell>
                  <TableCell align="center">Ratings</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {members.map((member) => (
                  <TableRow
                    key={member.id}
                    component={RouterLink}
                    to={`/sleeper/${member.sleeper_account?.sleeper_user_id}`}
                    sx={{
                      textDecoration: 'none',
                      '&:hover': { bgcolor: 'action.hover' },
                      cursor: 'pointer',
                    }}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar
                          src={
                            member.sleeper_account?.current_avatar_id
                              ? `https://sleepercdn.com/avatars/thumbs/${member.sleeper_account.current_avatar_id}`
                              : undefined
                          }
                          sx={{ width: 32, height: 32 }}
                        >
                          {member.sleeper_account?.current_username?.[0]?.toUpperCase()}
                        </Avatar>
                        <Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {member.sleeper_account?.current_username || 'Unknown'}
                            </Typography>
                            {member.sleeper_account?.is_verified && (
                              <VerifiedIcon sx={{ fontSize: 14, color: 'success.main' }} />
                            )}
                          </Box>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {member.team_name || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      {member.is_owner ? (
                        <Chip label="Commissioner" size="small" color="primary" />
                      ) : (
                        <Chip label="Member" size="small" variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {member.ratingStats ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                          <Rating
                            value={member.ratingStats.overall_average}
                            precision={0.1}
                            readOnly
                            size="small"
                          />
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {member.ratingStats.overall_average.toFixed(1)}
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No ratings
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2">
                        {member.ratingStats?.total_ratings || 0}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
}

